import * as React from 'react';
import { styled, useTheme, Theme, CSSObject } from '@mui/material/styles';
import Box from '@mui/material/Box';
import MuiDrawer from '@mui/material/Drawer';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import CssBaseline from '@mui/material/CssBaseline';
import IconButton from '@mui/material/IconButton';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import TopicOutlinedIcon from '@mui/icons-material/TopicOutlined';
import TopicIcon from '@mui/icons-material/Topic';
import ReportOutlinedIcon from '@mui/icons-material/ReportOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import { useNavigate } from 'react-router-dom';
import UserNavItem from './UserNavItem';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import BusinessIcon from '@mui/icons-material/Business';
import GroupsIcon from '@mui/icons-material/Groups';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import ListAltIcon from '@mui/icons-material/ListAlt';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined';
import ChevronRightOutlinedIcon from '@mui/icons-material/ChevronRightOutlined';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import BadgeIcon from '@mui/icons-material/Badge';
import DataObjectIcon from '@mui/icons-material/DataObject';
import { useLocation } from 'react-router-dom';
import OrbitoLogo from '../../images/Orbito_Logo_WhiteText.svg';
import OrbitoLogoDarkText from '../../images/Orbito_Logo_DarkText.svg';
import AddIcon from '@mui/icons-material/Add';
import Menu from '../common/Menu';
import Button from '../common/buttons/Button';
import { showModal } from '../../data/stores/modalStore';
import { useDispatch, useSelector } from 'react-redux';
import { isAdmin } from '../../utils/auth-utils';
import { AppDispatch, RootState } from '../../data/store';
import { useEffect, useMemo } from 'react';
import { Tooltip, useMediaQuery } from '@mui/material';
import { updateSelf } from '../../data/thunks/userThunk';
import { orbitoGradient } from '../../theme/theme';
import DrawerManageRoutes from './DrawerManageRoutes';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
const pjson = require('../../../package.json');
const VERSION = pjson.version;

const drawerWidth = 240;

const openedMixin = (theme: Theme): CSSObject => ({
	width: drawerWidth,
	transition: theme.transitions.create('width', {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.enteringScreen,
	}),
	overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
	transition: theme.transitions.create('width', {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.leavingScreen,
	}),
	overflowX: 'hidden',
	width: `calc(${theme.spacing(7)} + 1px)`,
	[theme.breakpoints.up('sm')]: {
		width: `calc(${theme.spacing(8)} + 1px)`,
	},
});

const DrawerHeader = styled('div')(({ theme }) => ({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'flex-start',
	padding: theme.spacing(0, 1),
	marginLeft: 15,
	marginTop: 10,
	cursor: 'pointer',
	// necessary for content to be below app bar
	...theme.mixins.toolbar,
}));

interface AppBarProps extends MuiAppBarProps {
	open?: boolean;
}

const AppBar = styled(MuiAppBar, {
	shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
	zIndex: theme.zIndex.drawer,
	transition: theme.transitions.create(['width', 'margin'], {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.leavingScreen,
	}),
	...(open && {
		marginLeft: theme.breakpoints.down('md') ? 0 : drawerWidth,
		width: theme.breakpoints.down('md')
			? '100%'
			: `calc(100% - ${drawerWidth}px)`,
		transition: theme.transitions.create(['width', 'margin'], {
			easing: theme.transitions.easing.sharp,
			duration: theme.transitions.duration.enteringScreen,
		}),
	}),
}));

const Drawer = styled(MuiDrawer, {
	shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
	width: drawerWidth,
	flexShrink: 0,
	whiteSpace: 'nowrap',
	boxSizing: 'border-box',
	...(open && {
		...openedMixin(theme),
		'& .MuiDrawer-paper': openedMixin(theme),
	}),
	...(!open && {
		...closedMixin(theme),
		'& .MuiDrawer-paper': closedMixin(theme),
	}),
}));

export default function MiniDrawer({
	children,
}: {
	children: React.ReactNode;
}) {
	const dispatch = useDispatch<AppDispatch>();
	const navigate = useNavigate();
	const location = useLocation();
	const {
		company: { subscription, googleDriveSetupVerified, initialized },
	} = useSelector((state: RootState) => state.companyState);
	const userTheme = useSelector(
		(state: RootState) => state.authState?.user?.preferences?.theme
	);
	const tourTooltipViewed = useSelector(
		(state: RootState) =>
			state.authState?.user?.helpers?.tourTooltipViewed ?? false
	);
	const theme = useTheme();
	const isLightMode = theme?.palette?.mode === 'light';
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));
	const [open, setOpen] = React.useState(true);
	const [mobileOpen, setMobileOpen] = React.useState(false);
	const [tooltipOpen, setTooltipOpen] = React.useState(false);
	const [manageExpanded, setManageExpanded] = React.useState(false);

	useEffect(() => {
		if (!tourTooltipViewed && !googleDriveSetupVerified) {
			setTooltipOpen(true);
		}
	}, [tourTooltipViewed, googleDriveSetupVerified]);

	const handleDrawerOpen = () => {
		setOpen(true);
	};

	const handleDrawerClose = () => {
		setOpen(false);
	};

	const handleDrawerToggle = () => {
		setMobileOpen(!mobileOpen);
	};

	const handleTooltipClose = () => {
		dispatch(updateSelf({ helpers: { tourTooltipViewed: true } }, false));
		setTooltipOpen(false);
	};

	const helpMenuItems = useMemo(() => {
		return [
			{
				display: 'Launch App Tour',
				onClick: () => {
					dispatch(showModal({ openId: 'tourModal' }));
					setTooltipOpen(false);
				},
			},
			{
				display: 'Getting Started',
				onClick: () => {
					window.open(
						'https://docs.google.com/document/d/1jhSGQLb2Y9TVCKCj1aTnbMAnSHdNP6omgFav1DTxXxo/edit?usp=drive_link',
						'_blank'
					);
					setTooltipOpen(false);
				},
			},
			{
				display: 'Guidelines and Best Practices',
				onClick: () => {
					window.open(
						'https://docs.google.com/document/d/1GUm73nsasEoS7bN0EwgybLB-1nSOZUZVx8y1oVHzSbg/edit?usp=drive_link',
						'_blank'
					);
					setTooltipOpen(false);
				},
			},
			// {
			// 	display: 'User Management',
			// 	onClick: () => {
			// 		window.open(
			// 			'https://docs.google.com/document/d/14zESMHo_tiCVO1gupw1eRO64qW5TtetJ60Hk6EnOms4/edit?usp=drive_link',
			// 			'_blank'
			// 		);
			// 		setTooltipOpen(false);
			// 	},
			// },
		];
	}, [dispatch]);

	const menuItems = React.useMemo(() => {
		return [
			{
				display: (
					<MenuItemLabel>
						Project <ChevronRightOutlinedIcon />
					</MenuItemLabel>
				),
				onClick: () => {
					if (!googleDriveSetupVerified) {
						dispatch(
							showModal({
								openId: 'googleWorkspaceVerificationModal',
							})
						);
					} else {
						setMobileOpen(false);
						dispatch(showModal({ openId: 'projectCreationModal' }));
					}
				},
				icon: (
					<TopicOutlinedIcon
						fontSize={'medium'}
						sx={{ marginRight: 1 }}
					/>
				),
			},
			{
				display: (
					<MenuItemLabel>
						Client <ChevronRightOutlinedIcon />
					</MenuItemLabel>
				),
				onClick: () => {
					if (!googleDriveSetupVerified) {
						dispatch(
							showModal({
								openId: 'googleWorkspaceVerificationModal',
							})
						);
					} else {
						setMobileOpen(false);
						dispatch(showModal({ openId: 'clientCreationModal' }));
					}
				},
				icon: (
					<GroupsOutlinedIcon
						fontSize={'medium'}
						sx={{ marginRight: 1 }}
					/>
				),
			},
			{
				display: (
					<MenuItemLabel>
						Staff <ChevronRightOutlinedIcon />
					</MenuItemLabel>
				),
				onClick: () => {
					setMobileOpen(false);
					dispatch(
						showModal({
							openId: 'userCreationModal',
							props: { initialCrewType: 'INTERNAL' },
						})
					);
				},
				icon: (
					<BadgeOutlinedIcon
						fontSize={'medium'}
						sx={{ marginRight: 1 }}
					/>
				),
			},
			{
				display: (
					<MenuItemLabel>
						Crew <ChevronRightOutlinedIcon />
					</MenuItemLabel>
				),
				onClick: () => {
					setMobileOpen(false);
					dispatch(
						showModal({
							openId: 'userCreationModal',
							props: { initialCrewType: 'EXTERNAL' },
						})
					);
				},
				icon: (
					<ManageAccountsOutlinedIcon
						fontSize={'medium'}
						sx={{ marginRight: 1 }}
					/>
				),
			},
			{
				display: (
					<MenuItemLabel>
						Group <ChevronRightOutlinedIcon />
					</MenuItemLabel>
				),
				onClick: () => {
					setMobileOpen(false);
					dispatch(
						showModal({
							openId: 'groupCreationModal',
						})
					);
				},
				icon: (
					<ManageAccountsOutlinedIcon
						fontSize={'medium'}
						sx={{ marginRight: 1 }}
					/>
				),
			},
		];
	}, [dispatch, googleDriveSetupVerified]);

	// TODO: Subscription
	// const hasActiveSubscription =
	// 	subscription?.status === 'active' ||
	// 	subscription?.status === 'pending_webhook' ||
	// 	subscription?.status === 'trialing' ||
	// 	subscription?.status === 'canceling';
	const hasActiveSubscription = true;

	const selectedButtonColor =
		// @ts-ignore
		userTheme === 'orbito' ? orbitoGradient : theme.palette.action.selected;

	return (
		<Box
			sx={{
				display: 'flex',
				backdropFilter: 'none',
				backgroundColor: 'transparent',
			}}
		>
			<CssBaseline />
			<AppBar
				position="fixed"
				open={open}
				sx={{
					backdropFilter: 'none',
				}}
			>
				<Toolbar
					sx={{
						justifyContent: isMobile ? 'space-between' : 'flex-end',
					}}
				>
					{isMobile && (
						<IconButton
							color="inherit"
							aria-label="open drawer"
							onClick={handleDrawerToggle}
							edge="start"
							sx={{
								marginRight: 2,
							}}
						>
							<MenuIcon />
						</IconButton>
					)}

					{isMobile && (
						<Box>
							<img
								src={
									isLightMode
										? OrbitoLogoDarkText
										: OrbitoLogo
								}
								style={{ height: 30 }}
							/>
						</Box>
					)}
					{!googleDriveSetupVerified && initialized && (
						<Box sx={{ display: 'flex', alignItems: 'center' }}>
							<IconButton
								onClick={() =>
									dispatch(
										showModal({
											openId: 'googleWorkspaceVerificationModal',
										})
									)
								}
								color="error"
							>
								<ReportOutlinedIcon />
							</IconButton>
						</Box>
					)}

					<Box sx={{ display: 'flex', alignItems: 'center' }}>
						<Tooltip
							open={tooltipOpen}
							onClose={handleTooltipClose}
							arrow
							placement="left"
							title="Click here to take a tour of Orbito"
						>
							<span>
								<Menu
									iconButton
									alternativeIcon={() => (
										<HelpOutlineOutlinedIcon />
									)}
									menuItems={helpMenuItems}
									anchorOrigin={{
										vertical: 'bottom',
										horizontal: 'right',
									}}
								/>
							</span>
						</Tooltip>
						<UserNavItem />
					</Box>
				</Toolbar>
			</AppBar>

			{/* Mobile drawer */}
			{isMobile && (
				<MuiDrawer
					variant="temporary"
					open={mobileOpen}
					onClose={handleDrawerToggle}
					ModalProps={{
						keepMounted: true, // Better mobile performance
					}}
					sx={{
						display: { xs: 'block', md: 'none' },
						'& .MuiDrawer-paper': {
							width: drawerWidth,
							backgroundColor: '#000000',
						},
						'& .MuiListItemText-primary': {
							color: '#ffffff !important',
							opacity: '1 !important',
						},
						'& .MuiListItemIcon-root': {
							color: '#ffffff !important',
						},
					}}
				>
					<List
						style={{
							height: '100%',
							display: 'flex',
							flexDirection: 'column',
							marginTop: 70,
						}}
					>
						{mainRoutes.map((route, index) => {
							if ('section' in route) {
								return (
									<ListItem
										key={route.section}
										sx={{
											display: 'block',
											px: 2,
											pt: index === 0 ? 1 : 3,
											pb: 0.5,
										}}
									>
										<Typography
											variant="caption"
											sx={{
												color: '#999999',
												fontWeight: 500,
												textTransform: 'uppercase',
												letterSpacing: '0.5px',
											}}
										>
											{route.section}
										</Typography>
									</ListItem>
								);
							}

							return (
								<ListItem
									key={route.text}
									disablePadding
									sx={{ display: 'block' }}
								>
									<ListItemButton
										onClick={() => {
											navigate(route.path);
											handleDrawerToggle();
										}}
										sx={{
											px: 1.5,
											cursor: 'pointer',
											background: isPathActive(
												route.path,
												location.pathname
											)
												? selectedButtonColor +
												  ' !important'
												: 'transparent',
										}}
										disabled={!hasActiveSubscription}
									>
										<ListItemIcon
											sx={{
												minWidth: 0,
												mr: 2,
												justifyContent: 'center',
												color: '#ffffff',
											}}
										>
											{isPathActive(
												route.path,
												location.pathname
											)
												? route.activeIcon
												: route.icon}
										</ListItemIcon>
										<ListItemText
											primary={route.text}
											sx={{
												color: '#ffffff',
												opacity: 1,
											}}
										/>
									</ListItemButton>
								</ListItem>
							);
						})}
						<ListItem style={{ flex: 1 }} />

						{/* New Button at the bottom of mobile drawer */}
						{isAdmin() && (
							<ListItem
								sx={{
									display: 'flex',
									justifyContent: 'center',
									padding: '10px 20px 20px 20px',
								}}
							>
								<Menu
									menuItems={menuItems}
									variant={'elevation'}
									color={'primary'}
									placement="top"
									disabled={!hasActiveSubscription}
									customButton={
										<Button
											onClick={() => {}}
											variant="contained"
											color="primary"
											style={{
												width: '100%',
												textTransform: 'none',
												backgroundColor:
													!hasActiveSubscription
														? theme.palette.action
																.disabledBackground
														: theme.palette
																.background
																.button,
												color: !hasActiveSubscription
													? theme.palette.action
															.disabled
													: theme.palette.text
															.primary,
												boxShadow:
													'0px 1px 6px rgba(0, 0, 0, 0.2)',
												borderRadius: '15px',
												padding: '12px 24px',
												border: '1px solid rgba(0, 0, 0, 0.12)',
												opacity: !hasActiveSubscription
													? 0.6
													: 1,
												cursor: !hasActiveSubscription
													? 'not-allowed'
													: 'pointer',
											}}
										>
											<AddIcon sx={{ mr: 1 }} /> New
										</Button>
									}
								/>
							</ListItem>
						)}
					</List>
				</MuiDrawer>
			)}

			{!isMobile && (
				<Drawer
					variant="permanent"
					open={open}
					sx={{
						backdropFilter: 'none',
						'& .MuiDrawer-paper': {
							backgroundColor: 'transparent !important',
							boxShadow: 'none',
						},
					}}
				>
					<DrawerHeader onClick={() => navigate('/')}>
						<img
							src={isLightMode ? OrbitoLogoDarkText : OrbitoLogo}
							style={{ height: 35 }}
						/>
						{/* {open && (
							<IconButton
								onClick={(e) => {
									e.stopPropagation();
									handleDrawerClose();
								}}
								sx={{ ml: 'auto', mr: 1 }}
							>
								<ChevronLeftIcon />
							</IconButton>
						)} */}
					</DrawerHeader>
					{isAdmin() && (
						<NewButtonContainer>
							<Menu
								menuItems={menuItems}
								variant={'elevation'}
								color={'primary'}
								disabled={!hasActiveSubscription}
								customButton={
									<Button
										onClick={() => {}}
										variant="contained"
										color="primary"
										sx={{ textTransform: 'none' }}
										style={{
											backgroundColor:
												!hasActiveSubscription
													? theme.palette.action
															.disabledBackground
													: theme.palette.background
															.button,
											color: !hasActiveSubscription
												? theme.palette.action.disabled
												: theme.palette.text.primary,
											boxShadow:
												'0px 1px 6px rgba(0, 0, 0, 0.2)',
											borderRadius: '15px',
											padding: '12px 24px',
											border: '1px solid rgba(0, 0, 0, 0.12)',
											cursor: !hasActiveSubscription
												? 'not-allowed'
												: 'pointer',
											opacity: !hasActiveSubscription
												? 0.6
												: 1,
										}}
									>
										<AddIcon /> New
									</Button>
								}
							/>
						</NewButtonContainer>
					)}
					<List
						style={{
							height: '100%',
							display: 'flex',
							flexDirection: 'column',
							marginTop: 15,
						}}
					>
						{mainRoutes.map((route, index) => {
							if ('section' in route) {
								return (
									<ListItem
										key={route.section}
										sx={{
											display: 'block',
											px: 2,
											pt: 1,
											pb: 1,
										}}
									>
										<Typography
											variant="caption"
											sx={{
												color: theme.palette.text
													.secondary,
												fontSize: 14,
												fontWeight: 500,
												letterSpacing: '0.5px',
												opacity: open ? 1 : 0,
												transition:
													theme.transitions.create(
														'opacity',
														{
															duration:
																theme
																	.transitions
																	.duration
																	.shorter,
														}
													),
											}}
										>
											{route.section}
										</Typography>
									</ListItem>
								);
							}

							return (
								<ListItem
									key={route.text}
									disablePadding
									sx={{ display: 'block' }}
								>
									<ListItemButton
										onClick={() => {
											navigate(route.path);
										}}
										sx={{
											// minHeight: 32,
											justifyContent: open
												? 'initial'
												: 'center',
											px: 1.5,
											cursor: 'pointer',
											background: isPathActive(
												route.path,
												location.pathname
											)
												? selectedButtonColor +
												  ' !important'
												: 'transparent',
										}}
										disabled={!hasActiveSubscription}
									>
										<ListItemIcon
											sx={{
												minWidth: 0,
												mr: open ? 1.25 : 'auto',
												justifyContent: 'center',
											}}
										>
											{isPathActive(
												route.path,
												location.pathname
											)
												? route.activeIcon
												: route.icon}
										</ListItemIcon>
										<ListItemText
											primary={route.text}
											sx={{ opacity: open ? 1 : 0 }}
										/>
									</ListItemButton>
								</ListItem>
							);
						})}
					</List>
				</Drawer>
			)}

			<Box
				component="main"
				sx={{
					flexGrow: 1,
					p: isMobile ? 1 : 3,
					paddingTop: '60px',
					position: 'fixed',
					top: 20,
					left: isMobile ? 0 : open ? 220 : 73,
					width: isMobile
						? '100%'
						: open
						? `calc(100% - 215px)`
						: 'calc(100% - 73px)',
					height: 'calc(100% - 15px)',
					transition: theme.transitions.create(['width', 'left'], {
						easing: theme.transitions.easing.sharp,
						duration: theme.transitions.duration.enteringScreen,
					}),
				}}
			>
				{children}
			</Box>
		</Box>
	);
}

const isPathActive = (path: string, currentPath: string) => {
	if (path === '/') {
		return currentPath === path;
	} else {
		return currentPath.includes(path);
	}
};

const mainRoutes = [
	{
		text: 'Dashboard',
		activeIcon: <DashboardIcon />,
		icon: <DashboardOutlinedIcon />,
		path: '/',
	},
	{
		text: 'Projects',
		activeIcon: <TopicIcon />,
		icon: <TopicOutlinedIcon />,
		path: '/projects',
	},
	{
		section: 'People',
	},
	{
		text: 'Clients',
		activeIcon: <BusinessIcon />,
		icon: <BusinessIcon />,
		path: '/crew/clients',
	},
	{
		text: 'Staff',
		activeIcon: <BadgeIcon />,
		icon: <BadgeOutlinedIcon />,
		path: '/crew/internal',
	},
	{
		text: 'Crew',
		activeIcon: <ManageAccountsIcon />,
		icon: <ManageAccountsOutlinedIcon />,
		path: '/crew/external',
	},
	{
		text: 'Groups',
		activeIcon: <GroupsIcon />,
		icon: <GroupsOutlinedIcon />,
		path: '/crew/groups',
	},
	{
		section: 'Templates',
	},
	{
		text: 'Orbito Templates',
		activeIcon: <InsertDriveFileIcon />,
		icon: <InsertDriveFileOutlinedIcon />,
		path: '/templates/orbito',
	},
	{
		text: 'Custom Templates',
		activeIcon: <NoteAddIcon />,
		icon: <NoteAddOutlinedIcon />,
		path: '/templates/custom',
	},
	{
		section: 'Project Settings',
	},
	{
		text: 'Project Types',
		activeIcon: <ListAltIcon />,
		icon: <ListAltIcon />,
		path: '/project-configuration/types',
	},
	{
		text: 'Project Sub Types',
		activeIcon: <FormatListBulletedIcon />,
		icon: <FormatListBulletedIcon />,
		path: '/project-configuration/sub-types',
	},
	{
		text: 'Project Data',
		activeIcon: <DataObjectIcon />,
		icon: <DataObjectIcon />,
		path: '/project-configuration/data',
	},
];

const NewButtonContainer = styled('div')`
	margin-left: 20px;
	margin-top: 20px;
`;

const MenuItemLabel = styled('div')`
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 175px;
`;
